/**
 * Blog post catalogue.
 *
 * Each entry contains both the metadata used for the listing/SEO and the
 * markdown body rendered with react-markdown on the post detail page.
 *
 * IMPORTANT for SEO:
 *  - slug must be lowercase-kebab and unique within `posts`
 *  - description = 140-160 char meta description
 *  - keywords = 6-10 long-tail terms
 *  - cover = absolute path under /img/...
 *  - content = full markdown, H2/H3 hierarchy, internal links where possible
 */

export const CATEGORIES = [
  {
    slug: "china-travel-guides",
    name: "China Travel Guides",
    description:
      "City-by-city travel guides to Beijing, Shanghai, Xi'an, Chengdu and beyond — itineraries, transport, and local tips for first-time visitors to China.",
    color: "#C62828",
  },
  {
    slug: "business-travel-to-china",
    name: "Business Travel to China",
    description:
      "Executive travel briefings, supplier sourcing trips, trade fair coverage and B2B logistics for companies travelling to China.",
    color: "#0E2A5C",
  },
  {
    slug: "factory-visits-in-china",
    name: "Factory Visits in China",
    description:
      "Practical guidance on visiting Chinese factories — verification, audits, interpreters, sample sourcing and supplier negotiation.",
    color: "#D4AF37",
  },
  {
    slug: "chinese-culture",
    name: "Chinese Culture",
    description:
      "Festivals, history, etiquette, art and food culture — context for travellers who want to understand China beyond the surface.",
    color: "#2E5D4B",
  },
  {
    slug: "chinese-language-programs",
    name: "Chinese Language Programs",
    description:
      "Mandarin learning programs in Beijing, Shanghai and Chengdu — HSK preparation, immersion options and accommodation guidance.",
    color: "#7A1F1F",
  },
  {
    slug: "education-study-tours",
    name: "Education & Study Tours",
    description:
      "School trips, university partnerships and curated educational tours combining academic visits with cultural exploration.",
    color: "#1FA3E0",
  },
  {
    slug: "visa-travel-updates",
    name: "Visa & Travel Updates",
    description:
      "Latest Chinese visa policies, transit rules, entry requirements and travel updates for international visitors — refreshed regularly.",
    color: "#C0392B",
  },
];

export const getCategory = (slug) => CATEGORIES.find((c) => c.slug === slug);

export const posts = [
  // 1. CHINA TRAVEL GUIDES
  {
    slug: "first-time-china-itinerary-10-days",
    category: "china-travel-guides",
    title: "First-Time in China: The Perfect 10-Day Itinerary",
    description:
      "A complete 10-day China itinerary covering Beijing, Xi'an and Shanghai — flights, hotels, daily activities and local tips for first-time travellers.",
    excerpt:
      "From the Great Wall to the Bund — a balanced 10-day route that introduces newcomers to China's history, food and modern energy.",
    keywords: [
      "china itinerary",
      "first time china",
      "10 day china trip",
      "beijing xian shanghai",
      "china travel guide",
      "great wall itinerary",
    ],
    cover: "/img/destinations/xian.png",
    author: "Mei Liang",
    date: "2026-01-12",
    readMinutes: 9,
    content: `## Why this route works

Three cities, ten days, zero overwhelm. Beijing for imperial history, Xi'an for the Terracotta Army and the Silk Road, Shanghai for skylines and modern China. High-speed trains connect them in 4-6 hours each, so you spend more time exploring and less time in transit.

## Day-by-day overview

### Days 1-4 · Beijing
- **Day 1** Arrive, check in to a Wangfujing hotel, evening walk around Qianmen.
- **Day 2** Forbidden City + Tiananmen Square + Jingshan Park sunset.
- **Day 3** **Great Wall at Mutianyu** — quieter than Badaling, cable car up, toboggan down.
- **Day 4** Temple of Heaven + Houhai Lake hutong tour by rickshaw.

### Days 5-6 · Xi'an (high-speed train, ~4.5 h)
- **Day 5** Terracotta Warriors half-day, ancient city wall by bike at sunset.
- **Day 6** Muslim Quarter food crawl, Big Wild Goose Pagoda fountain show.

### Days 7-10 · Shanghai (high-speed train, ~6 h)
- **Day 7** Yu Garden + Old Town, evening on the Bund.
- **Day 8** French Concession walking tour, M50 art district.
- **Day 9** Day trip to **Zhujiajiao water town** or Suzhou.
- **Day 10** Shopping on Nanjing Road, depart from Pudong.

## What it costs

| Budget tier | Per person | Hotels |
|---|---|---|
| Comfort | USD 1,800 – 2,500 | 4★ central |
| Premium | USD 3,000 – 4,500 | 5★ landmark |
| Luxury  | USD 6,000 + | Aman / Peninsula |

Prices include domestic transport, breakfasts, guided days and entrance tickets. Excludes international flights.

## Booking tip

China's 240-hour visa-free transit covers most travellers visiting Beijing → Shanghai. Our team handles all paperwork — [request a tailored 10-day proposal](/#contact).

> Travel light, pack layers, and download a VPN before you fly. The rest, we'll handle.`,
  },
  {
    slug: "beijing-vs-shanghai-which-city",
    category: "china-travel-guides",
    title: "Beijing vs Shanghai: Which Chinese City Should You Visit First?",
    description:
      "Choosing between Beijing and Shanghai? A side-by-side guide to history, food, business, nightlife and which city suits your travel style.",
    excerpt:
      "One is the soul of imperial China. The other is its electric future. Here's how to pick — or why you should see both.",
    keywords: [
      "beijing vs shanghai",
      "which chinese city to visit",
      "shanghai travel",
      "beijing travel",
      "china travel comparison",
    ],
    cover: "/img/destinations/shanghai.png",
    author: "James Howard",
    date: "2026-01-19",
    readMinutes: 7,
    content: `## The 30-second answer

- **Beijing** = history, food and political weight. Pick it if you want the Great Wall, the Forbidden City and Peking duck.
- **Shanghai** = skyline, business, design. Pick it if you want the Bund, art deco architecture and world-class restaurants.

If you have **5 days or more, see both** — they are only 4.5 hours apart by high-speed rail.

## Beijing — capital of history

Beijing rewards the curious. Wake up early for **Tiananmen flag-raising**, spend mornings inside the **Forbidden City**, afternoons in the **hutongs** behind Houhai. Day trips to **Mutianyu Great Wall** and the **Summer Palace** are non-negotiable.

The food is heavier, the streets wider, the pace slower. Allow at least 4 nights.

## Shanghai — capital of the future

Shanghai is China's most international city. The **Bund** at night, **Pudong** skyline at dawn, **French Concession** lanes by day. It is a city built for walking and eating — speakeasies in the former French quarter, dumpling houses in the Old Town, world-class fine dining in Jing'an.

Shanghai is also the best base for **business travel** — most factory visits in the Yangtze Delta start here. See our [China Business Explorer](/china-business-explorer) for B2B itineraries.

## Quick comparison

| | Beijing | Shanghai |
|---|---|---|
| Best for | History, culture | Business, design |
| Pace | Deliberate | Electric |
| Food highlight | Peking duck | Xiaolongbao |
| English level | Moderate | High |
| Ideal stay | 4 nights | 3 nights |

## Verdict

First-timers: do **4 nights Beijing + 3 nights Shanghai**. It's the most-requested itinerary we run, and it never disappoints. [Talk to our team](/#contact) to scope your version.`,
  },

  // 2. BUSINESS TRAVEL TO CHINA
  {
    slug: "executive-business-travel-to-china-2026",
    category: "business-travel-to-china",
    title: "Executive Business Travel to China in 2026: A Complete Briefing",
    description:
      "An executive briefing for business travel to China in 2026 — visas, hotels, ground logistics, interpreters and what's changed post-pandemic.",
    excerpt:
      "Six trip-ready insights for executives heading to China this year: from 240-hour visa-free transit to Mandarin-ready meeting rooms.",
    keywords: [
      "business travel to china",
      "executive travel china",
      "china business trip 2026",
      "china visa business",
      "supplier visit china",
    ],
    cover: "/img/destinations/shanghai_business.png",
    author: "James Howard",
    date: "2026-01-22",
    readMinutes: 10,
    content: `## What changed in 2026

China's **240-hour visa-free transit** now covers most GCC, EU and APAC passport holders. Combined with the **APEC Business Travel Card**, executives can move between Beijing, Shanghai, Shenzhen and Guangzhou with almost zero friction.

## The 6-point briefing

### 1. Visa and entry
Most business travellers from the UAE, EU, UK and Australia can use **240-hour visa-free transit** for trips routing through 50+ Chinese cities. For longer stays or repeat trips, the **M visa** (business) is the gold standard.

### 2. Money and payments
Cash is dead. WeChat Pay and Alipay are essential. We pre-load both with international cards before your arrival.

### 3. Connectivity
Bring an eSIM with built-in VPN, or use our **China Business Explorer SIM** — keeps your Gmail, LinkedIn and Slack alive across the country.

### 4. Hotels
- **Beijing**: Rosewood, Mandarin Oriental Wangfu, Peninsula
- **Shanghai**: Bulgari, Amanyangyun, Capella Jian Ye Li
- **Shenzhen**: Mandarin Oriental, Four Seasons

### 5. Ground logistics
Private cars with English/Arabic speaking drivers, interpreters on standby for factory and supplier meetings, and dedicated trip managers reachable on WhatsApp 24/7.

### 6. Cultural calibration
Lunch meetings end at 1pm sharp. Business cards exchanged with both hands. Toasts are made standing. Small gestures, big signals.

## What a typical trip looks like

A **5-day mission** combining 2 factory visits, 3 supplier meetings, 1 trade fair day and 1 networking dinner. See our [sample business journey on the China Business Explorer page](/china-business-explorer).

## Ready to scope your trip?

We design every brief around your meeting list, factory shortlist and travel dates. [Request a tailored business proposal](/china-business-explorer#business-inquiry) — we respond within 24 hours.`,
  },
  {
    slug: "canton-fair-2026-complete-guide",
    category: "business-travel-to-china",
    title: "The Canton Fair 2026: A Buyer's Complete Guide",
    description:
      "Everything you need to attend the 2026 Canton Fair in Guangzhou — phases, hotels, badge pickup, interpreters and supplier sourcing strategy.",
    excerpt:
      "The world's largest trade fair, decoded. Phase calendar, smart hotel choices, badge tips and a buyer's playbook from our on-the-ground team.",
    keywords: [
      "canton fair 2026",
      "guangzhou trade fair",
      "canton fair buyer guide",
      "china sourcing trip",
      "canton fair phases",
    ],
    cover: "/img/cuisine/hunan_pork.png",
    author: "Wei Zhang",
    date: "2026-01-29",
    readMinutes: 8,
    content: `## What is the Canton Fair

The **China Import and Export Fair (Canton Fair)** is the world's largest trade show — three phases, 25,000+ exhibitors, every major Chinese manufacturer in one place.

## 2026 phases (provisional)

- **Phase 1 (15-19 Apr)** — Electronics, machinery, building materials, vehicles.
- **Phase 2 (23-27 Apr)** — Consumer goods, home, gifts, decor.
- **Phase 3 (01-05 May)** — Textiles, footwear, office, medical, food.

If you source across categories, plan **2 phases back-to-back** with a rest day in between.

## Where to stay

- **Garden Hotel** — official, but books out a year ahead.
- **Shangri-La Guangzhou** — across the river, free shuttle to Pazhou.
- **The Ritz-Carlton Guangzhou** — best for evening client dinners.

Avoid hotels north of the Pearl River during fair weeks — traffic doubles.

## The buyer's playbook

1. **Pre-fair**: We send you a curated list of pre-vetted exhibitors aligned with your sourcing brief.
2. **Day 1**: Survey halls, mark must-revisit booths, gather catalogues.
3. **Day 2-3**: Deep-dive meetings with 6-10 finalists. Interpreters on standby.
4. **Day 4**: Factory visits within 2 hours of Guangzhou — Foshan, Dongguan, Shenzhen.
5. **Day 5**: Negotiation and LOI signing at our office.

## Logistics we handle

- VIP badge collection (saves 3+ hours on Day 1)
- Bilingual interpreters (English / Arabic / Mandarin)
- Private cars with cooler boxes for samples
- Sample shipping to your home country

## Get a Canton Fair package

Our [Canton Fair package](/#cantonfair) bundles flights, hotel, interpreter, factory visits and sample shipping. [Request a 2026 quote](/china-business-explorer#business-inquiry).`,
  },

  // 3. FACTORY VISITS IN CHINA
  {
    slug: "how-to-visit-a-chinese-factory",
    category: "factory-visits-in-china",
    title: "How to Visit a Chinese Factory: A Buyer's Field Guide",
    description:
      "A practical field guide to visiting Chinese factories — from supplier verification and interpreters to QC checklists and negotiation tips.",
    excerpt:
      "What to ask, what to bring, what to look for. A boots-on-the-ground guide from our China sourcing team.",
    keywords: [
      "factory visit china",
      "china supplier verification",
      "visit chinese factory",
      "china sourcing trip",
      "factory audit china",
    ],
    cover: "/img/cuisine/xinjiang_lamb.png",
    author: "Wei Zhang",
    date: "2026-02-02",
    readMinutes: 11,
    content: `## Before you board the plane

The trip is only as good as the prep. Two weeks out, we run:

1. **Supplier verification** — business licence, export licence, factory address (real, not a trading company).
2. **Reference checks** — three current overseas customers, ideally in your category.
3. **Sample evaluation** — at least one round of production samples in your hands before flying.

If a "factory" cannot provide all three, it's almost always a trading company.

## On the day — what to bring

- Printed sourcing brief with target prices and MOQs
- Tape measure, calipers, lux meter (for QC)
- Phone with WeChat installed and topped up
- Local interpreter — never rely on the supplier's

## What to look for on the floor

- **Cleanliness and order** — a sloppy floor is a sloppy product.
- **Worker tenure** — ask the line leader how long they have worked there. Stability matters.
- **Test equipment** — calibration stickers, in-date, used.
- **Raw material storage** — labelled, dated, FIFO.
- **Outgoing QC** — is there a separate QC room? Reports on file?

## Negotiation, the short version

Chinese factory pricing has three layers: **list, friendly, and committed-volume**. Most foreign buyers leave 8-15% on the table by not asking for the third tier.

We coach our clients to walk in with:
- Annual volume commitment in writing
- A clear payment structure (30/70 LC is the new normal)
- A back-up supplier (always — never single-source from China)

## Common pitfalls

- Accepting MOQs at face value (negotiable in 80% of cases)
- Paying 100% upfront (don't — 30/70 is standard)
- Skipping the interpreter for "easy" English speakers (you will misread the negotiation)
- Letting the factory book your hotel (they will, and you will pay 2x retail)

## Let us handle it

Our [factory visits service](/china-business-explorer) covers verification, transport, interpretation and QC handover. [Request a quote](/china-business-explorer#business-inquiry).`,
  },
  {
    slug: "verifying-a-chinese-supplier-checklist",
    category: "factory-visits-in-china",
    title: "Verifying a Chinese Supplier: A 12-Point Due Diligence Checklist",
    description:
      "A 12-point due diligence checklist for verifying Chinese suppliers before you place a purchase order — covering legal, financial and operational risk.",
    excerpt:
      "Twelve checks that take a weekend and save you six figures. Our standard supplier verification protocol, made public.",
    keywords: [
      "verify chinese supplier",
      "china supplier due diligence",
      "is my chinese supplier real",
      "china supplier scam",
      "supplier verification china",
    ],
    cover: "/img/cuisine/peking_duck.png",
    author: "Wei Zhang",
    date: "2026-02-05",
    readMinutes: 9,
    content: `## Why this matters

A trading company misrepresenting itself as a factory will cost you 8-15% in margin and 2-4 weeks in lead time per order. Worse, a fraudulent "supplier" can disappear with a 30% deposit. The checks below take a weekend and a few hundred USD to run.

## The 12-point checklist

### Legal (4 checks)
1. **Business licence number** — verify on \`gsxt.gov.cn\` (China's national enterprise registry).
2. **Registered capital** — under USD 100k usually signals a trading company.
3. **Scope of business** — must include manufacturing in your category.
4. **Export licence** — confirm directly with the local Customs office.

### Financial (3 checks)
5. **Bank reference** — request a current relationship letter from their bank.
6. **D&B credit report** — USD 200, takes 5 days, worth every cent.
7. **VAT invoice samples** — confirms they're a registered tax payer with real revenue.

### Operational (5 checks)
8. **Factory address on Baidu Maps** — and Google Earth historical view. Is the building actually there? Year over year?
9. **Worker headcount on social security records** — a real factory has 50+ insured workers.
10. **Production capacity** vs. industry benchmarks for your product.
11. **In-house testing equipment** — photos and certificates.
12. **Customer references from your market** — at least 3, verified by you on a call.

## Red flags

- Email domain is Gmail / QQ / 163 instead of company domain
- Address is in a city famous for trading (Yiwu, Shenzhen Huaqiangbei) but they claim to manufacture
- WeChat-only communication, refuses video calls of the floor
- Quoted price is 30%+ below market

## When to skip self-verification

If your order value is **over USD 100,000**, hire a local audit firm or our team to run an on-the-ground inspection. The fee is recovered on a single avoided mistake.

## Get an audit

We run third-party supplier audits across China — verification, factory inspection, QC handover. [Request a quote](/china-business-explorer#business-inquiry).`,
  },

  // 4. CHINESE CULTURE
  {
    slug: "chinese-new-year-foreigners-guide",
    category: "chinese-culture",
    title: "Chinese New Year: A Foreigner's Guide to the World's Largest Festival",
    description:
      "Chinese New Year explained for foreign travellers — when it happens, what it means, what to do, what to avoid, and where to celebrate it.",
    excerpt:
      "Forty days, three billion journeys, one ancient story. A traveller's guide to the world's largest annual migration.",
    keywords: [
      "chinese new year guide",
      "spring festival china",
      "chinese new year for foreigners",
      "lunar new year china",
      "visit china during spring festival",
    ],
    cover: "/img/destinations/xian.png",
    author: "Mei Liang",
    date: "2026-01-25",
    readMinutes: 8,
    content: `## What it is

**Spring Festival** (chunjie 春节) marks the start of the lunar new year. It is China's most important holiday — a 15-day celebration that begins on Lunar New Year's Eve and ends with the Lantern Festival.

## When it happens

The date moves between **21 January and 20 February**. In 2026, the festival begins **17 February**. The official public holiday is one week, but most workers travel home for two weeks or more.

## What it means

It is a **family festival**, not a tourist one. The largest annual human migration on the planet — over 3 billion trips — happens around it. Domestic flights and trains book out three months in advance.

## Where to celebrate

- **Beijing** — Temple fairs at Ditan and Longtan, fireworks now restricted but still magical.
- **Xi'an** — Lantern displays on the ancient city wall.
- **Hong Kong** — A spectacular parade on Lunar New Year's Day, fireworks over Victoria Harbour on Day 2.
- **Pingyao** (Shanxi) — Old-China lanterns and traditional ceremonies in a UNESCO village.

## What to do

- Wear **red** — the colour of luck.
- Bring **small gifts** (fruit, tea) when invited to a home.
- Accept **red envelopes** (hongbao) with both hands.
- Try **dumplings** on Lunar New Year's Eve, **niangao** (sticky rice cake) on Day 1.

## What to avoid

- Don't visit on Lunar New Year's Eve unless you have a host — most restaurants close.
- Don't book domestic travel without pre-booked train/plane tickets.
- Don't gift clocks, sharp objects or anything in groups of 4 (unlucky).

## What it feels like

Picture every shop window red, every doorframe pasted with calligraphy couplets, every restaurant serving a 12-dish reunion banquet. The energy is the closest thing to Christmas + New Year combined — louder, brighter, older.

## Travel tip

If you want to **see Spring Festival but skip the crowds**, fly into Hong Kong or Macau, then take the high-speed rail into Guangzhou or Shenzhen on Day 3. By Day 5, the country is back open. [Talk to us](/#contact) to plan a Spring Festival itinerary.`,
  },
  {
    slug: "tea-culture-china-beginner-guide",
    category: "chinese-culture",
    title: "Tea Culture in China: A Beginner's Guide to 4,000 Years of Brewing",
    description:
      "An introduction to Chinese tea culture — the six tea families, regional varieties, tea ceremonies, etiquette, and where to taste tea like a local.",
    excerpt:
      "Six tea families, hundreds of varieties, four thousand years of ceremony. A foreigner's first sip into the world of Chinese tea.",
    keywords: [
      "chinese tea culture",
      "china tea guide",
      "tea ceremony china",
      "longjing tea",
      "pu erh tea",
      "chinese tea house",
    ],
    cover: "/img/cuisine/mapo_tofu.png",
    author: "Mei Liang",
    date: "2026-02-01",
    readMinutes: 7,
    content: `## The six families

Chinese tea is grouped into six families based on oxidation:

1. **Green** (e.g. Longjing, Bi Luo Chun) — fresh, vegetal, lowest oxidation.
2. **White** (e.g. Bai Hao Yin Zhen) — delicate, mellow, sun-dried.
3. **Yellow** (e.g. Junshan Yinzhen) — rare, slightly oxidised.
4. **Oolong** (e.g. Tieguanyin, Da Hong Pao) — semi-oxidised, the connoisseur's favourite.
5. **Red / Black** (e.g. Keemun, Lapsang Souchong) — fully oxidised.
6. **Dark / Pu'erh** — post-fermented, aged for years, deeply earthy.

## Regions to taste them

- **Hangzhou (West Lake)** — Longjing green tea, picked from terraced hillsides.
- **Fujian** — Wuyi rock oolongs and Anxi Tieguanyin.
- **Yunnan** — Pu'erh from ancient tea forests.
- **Anhui** — Keemun red and Huangshan Maofeng green.

## The ceremony, simplified

Chinese **gongfu** tea service uses small clay pots, tiny cups, and multiple short infusions of the same leaves. Each pour reveals a different layer of the tea's character. There is no choreography to fear — slowness is the point.

## Etiquette at the tea table

- **Tap the table** twice with two fingers when the host pours your cup. It's a silent thank-you.
- **Never** lift the lid of the pot yourself — that's the host's job.
- **Sip slowly** — Chinese tea is meant to be tasted in three: aroma, body, finish.

## Where to go

- **Mei Jia Wu Tea Village** (Hangzhou) — green tea at the source.
- **Lao She Teahouse** (Beijing) — traditional teahouse with Peking opera evenings.
- **Heng Long Teahouse** (Chengdu) — sit in a bamboo chair, watch the city pass by.

## Add it to your itinerary

Most of our [cultural tours](/#culture) include at least one tea-house experience. [Tell us your style](/#contact) — green and floral, oolong and bold, or pu'erh aged in a Yunnan cave.`,
  },

  // 5. CHINESE LANGUAGE PROGRAMS
  {
    slug: "best-mandarin-programs-in-china",
    category: "chinese-language-programs",
    title: "The Best Mandarin Programs in China for International Students",
    description:
      "A 2026 guide to the best Mandarin learning programs in China — Beijing Language University, Tsinghua, Fudan and immersion schools in Chengdu and Shanghai.",
    excerpt:
      "Where to study Mandarin in China, how much it costs, and what type of program suits your level and goals.",
    keywords: [
      "learn mandarin in china",
      "best mandarin programs china",
      "study mandarin beijing",
      "chinese language school china",
      "HSK preparation china",
    ],
    cover: "/img/destinations/shanghai.png",
    author: "Mei Liang",
    date: "2026-01-30",
    readMinutes: 9,
    content: `## Pick a city first, then a school

Your city choice shapes 70% of your Mandarin journey:

- **Beijing** — Standard accent, best universities, most students.
- **Shanghai** — Modern, international, slightly accented Mandarin.
- **Chengdu** — Affordable, friendly, slower pace, great food.
- **Kunming** — Year-round mild weather, smallest expat bubble.

## Top universities

### Beijing Language and Culture University (BLCU)
The "Little United Nations" — purpose-built for teaching Mandarin to foreigners since 1962. Strong HSK prep, large international cohorts. Best for: serious learners, HSK 4-6 candidates.

### Tsinghua University
Highly selective, academic. Great for university-bound students preparing for a Chinese-medium degree.

### Fudan University (Shanghai)
Strong general Mandarin program with the bonus of being in Shanghai. Best for: students who want city life + study.

### Sichuan University (Chengdu)
Affordable, well-regarded, more relaxed pace. Best for: gap-year and semester students.

## Private immersion schools

Smaller class sizes (4-6 students), faster results, higher cost. **LTL Mandarin School** and **That's Mandarin** have campuses in multiple cities.

## What it costs (semester, USD)

| Program | Tuition | Total (with housing) |
|---|---|---|
| BLCU semester | ~1,900 | ~5,500 |
| Tsinghua semester | ~2,800 | ~7,500 |
| Private immersion (3 months) | ~4,000 | ~8,000 |
| 1-on-1 intensive (2 weeks) | ~1,200 | ~2,200 |

## Visa

X1 visa for 6+ months of study, X2 for shorter programs. We handle the application end-to-end as part of every program package.

## Our recommendation

For **first-time learners**: 12-week summer program at BLCU in Beijing or LTL in Chengdu.

For **business professionals**: 4-week 1-on-1 intensive in Shanghai, mornings only, with afternoons for networking.

[Tell us your goal](/#contact) and we'll match you to a program in 24 hours.`,
  },
  {
    slug: "hsk-explained-which-level-do-i-need",
    category: "chinese-language-programs",
    title: "HSK Explained: Which Mandarin Level Do You Actually Need?",
    description:
      "The HSK Mandarin proficiency test explained — all six levels, what each unlocks, study hours required, and which level is right for your goal.",
    excerpt:
      "From HSK 1 (200 words) to HSK 6 (5,000+ words) — a clear-eyed look at what each level means and how long it takes to get there.",
    keywords: [
      "HSK levels explained",
      "HSK test guide",
      "which HSK level do I need",
      "HSK 4 jobs",
      "HSK 6 university",
      "chinese proficiency test",
    ],
    cover: "/img/destinations/zhangjiajie.png",
    author: "Mei Liang",
    date: "2026-02-04",
    readMinutes: 6,
    content: `## What is the HSK

**Hanyu Shuiping Kaoshi** (HSK) is the official Chinese-government Mandarin proficiency test for foreigners. There are six levels — HSK 1 (beginner) to HSK 6 (advanced).

## The 6 levels at a glance

| Level | Vocab | Study hours* | Equivalent to |
|---|---|---|---|
| HSK 1 | ~150 | 60 | A1 |
| HSK 2 | ~300 | 120 | A2 |
| HSK 3 | ~600 | 240 | B1 |
| HSK 4 | ~1,200 | 400 | B2 |
| HSK 5 | ~2,500 | 800 | C1 |
| HSK 6 | ~5,000+ | 1,200+ | C2 |

*Estimates for native English speakers studying full-time.

## Which level do you actually need?

- **Tourist confidence** → HSK 2 (greetings, ordering food, taxis).
- **Long-term resident** → HSK 4 (work emails, light news reading).
- **Business meetings** → HSK 5 (negotiations with a translator on standby).
- **Chinese-medium university** → HSK 5 minimum, HSK 6 for top schools.
- **Government / diplomatic** → HSK 6.

## New HSK 3.0 (rolled out 2025-2026)

China is transitioning to a new 9-level system (HSK 3.0). Levels 1-6 still exist but are being remapped. For 2026, the old 6-level certificate is still widely accepted by universities and employers — don't wait for the new format.

## Where to test

Test centres exist in 100+ countries, including Dubai, Riyadh, London, Sydney and most major US cities. Test fee: USD 25-90 depending on level.

## How fast can you get there?

Realistic for a motivated learner with a good program:

- HSK 2 in **3 months**
- HSK 4 in **9-12 months**
- HSK 6 in **2-3 years**

## Our programs

We pair students with the right [Mandarin program](/#education) based on target HSK and timeline. [Get a personalised plan](/#contact).`,
  },

  // 6. EDUCATION & STUDY TOURS
  {
    slug: "school-study-tour-to-china-planning",
    category: "education-study-tours",
    title: "Planning a School Study Tour to China: A Headmaster's Checklist",
    description:
      "A planning checklist for school study tours to China — itineraries, safety, visas, accommodation, academic visits and cost benchmarks per student.",
    excerpt:
      "Twelve months out, three months out, departure day. A timeline-based checklist for headmasters and trip leaders planning a school tour to China.",
    keywords: [
      "school study tour china",
      "school trip to china",
      "educational tour china",
      "student trip china planning",
      "china school program",
    ],
    cover: "/img/destinations/xian.png",
    author: "James Howard",
    date: "2026-02-03",
    readMinutes: 10,
    content: `## Twelve months out

- Set **trip dates** (avoid Spring Festival, Golden Week, summer monsoons).
- Confirm **chaperone ratios** (we recommend 1:8 for under-15s, 1:12 for older).
- Begin **fundraising** and **parent meetings**.
- Issue an **expression of interest** to families.

## Six months out

- Lock **flights** — group fares with a partner OTA, full refund window.
- Choose your **academic theme**: language immersion, STEM/industry, arts/culture, history.
- Shortlist **partner schools or universities** in your target city.
- Decide on **insurance** — must include emergency evacuation.

## Three months out

- Collect **passports** and submit **L visa group applications**.
- Send **packing list** and **cultural briefing pack** to families.
- Confirm **dietary requirements** and special needs.
- Finalise **risk assessment** document.

## One month out

- Conduct **parent briefing evening** with the trip lead and an external speaker who knows China.
- Pre-load **WeChat** on every student and chaperone phone.
- Set up **emergency contact tree**.
- Finalise **rooming list** with same-gender pairings.

## On the ground

We provide:
- 24/7 bilingual trip manager
- Private bus with seatbelts and English-speaking driver
- Vetted hotels with single-floor block bookings
- Daily WhatsApp parent update with photos
- On-call doctor and translator service

## Cost benchmarks (per student, 8 nights)

| Tier | Cost | Includes |
|---|---|---|
| Standard | USD 2,400 | 3★ hotels, group transport |
| Premium | USD 3,400 | 4★ hotels, smaller groups, more inclusions |
| Bespoke | USD 4,800+ | Private classes, family stays, custom academic content |

Excludes international flights. Group discounts apply at 20+ students.

## Get a proposal

Our [education & study tours team](/#education) has designed China trips for schools across the GCC, UK and Australia. [Request a proposal](/#contact).`,
  },
  {
    slug: "stem-study-tour-china-shenzhen-beijing",
    category: "education-study-tours",
    title: "STEM Study Tours in China: From Shenzhen Labs to Beijing Tech Parks",
    description:
      "A guide to STEM-focused study tours in China — visiting Shenzhen tech campuses, Beijing AI labs, Hangzhou e-commerce parks and Shanghai biotech.",
    excerpt:
      "The science and technology trip that no Western university can match — direct access to China's labs, founders and innovation parks.",
    keywords: [
      "stem study tour china",
      "shenzhen tech tour",
      "beijing ai tour",
      "china innovation tour",
      "student tech trip china",
    ],
    cover: "/img/cuisine/xiaolongbao.png",
    author: "James Howard",
    date: "2026-02-06",
    readMinutes: 8,
    content: `## Why STEM tours land harder in China

China graduates 4.7 million STEM students a year. Cities are built around innovation parks. Students who visit don't just see labs — they see **scale, speed, and a different model of how science meets industry**.

## A typical 8-day STEM circuit

### Days 1-3 · Shenzhen
- **DJI campus** demo and Q&A
- **Tencent Binhai HQ** tour
- **Huaqiangbei** electronics market deep-dive
- **Maker Bay** prototyping workshop

### Days 4-5 · Hangzhou
- **Alibaba DAMO Academy** AI labs
- **NetEase HQ**
- **Cloud Town** innovation district tour

### Days 6-8 · Beijing
- **Tsinghua University** engineering labs
- **Zhongguancun** ("China's Silicon Valley") founders meeting
- **Beijing Genomics Institute** (BGI) lab tour
- **Forbidden City** half-day to balance the brain

## What students take home

- A working drone or maker project from Shenzhen
- A pitch deck reviewed by a Chinese founder
- A signed certificate from a partner university
- Photos, videos and a story that elevates their college application

## Logistics we handle

- All campus access permits (8-12 weeks lead time)
- Bilingual STEM-fluent guides
- Hotel block in the innovation district (saves 2 hours of daily transit)
- Group medical and travel insurance

## Cost (per student, 8 nights, 25-student group)

- **Standard**: USD 3,400
- **Premium**: USD 4,600

Includes everything except international flights.

## Lead time

Plan **6 months ahead** — campus access permits are the slow lane. [Request a STEM tour proposal](/#contact).`,
  },

  // 7. VISA & TRAVEL UPDATES
  {
    slug: "china-240-hour-visa-free-transit-2026",
    category: "visa-travel-updates",
    title: "China's 240-Hour Visa-Free Transit: What's New in 2026",
    description:
      "China's 240-hour visa-free transit policy explained — eligible passports, eligible cities, what you can do, what you can't, and how to plan a transit trip.",
    excerpt:
      "Ten days in China, no visa needed. Here's the updated 2026 rulebook for one of the easiest ways to visit the country.",
    keywords: [
      "china 240 hour visa free",
      "china transit visa 2026",
      "visa free china",
      "china tourism visa policy",
      "china travel updates",
    ],
    cover: "/img/destinations/shanghai.png",
    author: "James Howard",
    date: "2026-01-15",
    readMinutes: 6,
    content: `## What it is

The **240-hour (10-day) visa-free transit policy** lets eligible foreign travellers visit China without a visa, provided they enter and exit through specific ports and have an onward flight to a third country.

## Who's eligible

Passport holders from **54 countries** including: UAE, Saudi Arabia, Qatar, Kuwait, Bahrain, Oman, USA, UK, Canada, Australia, New Zealand, all EU member states, Japan, South Korea, Singapore.

## Where you can land

The policy now covers **60 ports of entry** across 24 provinces, including:
- **Beijing** (PEK, PKX)
- **Shanghai** (PVG, SHA)
- **Guangzhou** (CAN)
- **Shenzhen** (SZX)
- **Chengdu** (CTU)
- **Xi'an** (XIY)
- **Kunming** (KMG)

## What you can do

- Travel **freely between covered provinces** (no longer limited to a single city).
- Stay up to **240 hours (10 days)** from the day after arrival.
- Combine business meetings, tourism, family visits.

## What you cannot do

- Work or study formally (you still need M / X visas for that).
- Enter through a non-eligible port (e.g. some border crossings).
- Stay beyond 240 hours — exit and re-entry resets the clock.

## How to qualify

1. Hold a passport from one of the 54 eligible countries.
2. Have a **confirmed onward ticket** to a third country (not back to your origin in most cases).
3. Fill the arrival card and present the ticket at immigration.

## Practical tip

Our team pre-files the **temporary residence registration** (mandatory within 24 hours of arrival) and the entry plan with your airline, eliminating 95% of the friction at the border. [Request a transit-friendly itinerary](/#contact).`,
  },
  {
    slug: "china-tourist-l-visa-application-guide",
    category: "visa-travel-updates",
    title: "China Tourist (L) Visa Application Guide for 2026",
    description:
      "Step-by-step guide to applying for a Chinese tourist (L) visa in 2026 — documents, invitation letters, photo requirements, fees and processing times.",
    excerpt:
      "Planning more than 10 days in China? The L visa is straightforward — if you know the documents, photo specs and where to apply.",
    keywords: [
      "china tourist visa",
      "china L visa application",
      "how to apply china visa",
      "china visa documents 2026",
      "china visa for travellers",
    ],
    cover: "/img/destinations/xian.png",
    author: "Mei Liang",
    date: "2026-02-07",
    readMinutes: 7,
    content: `## When to use the L visa

Use the **tourist (L) visa** when your trip is **longer than 10 days**, or when you plan to enter through a port not covered by visa-free transit. Single, double and multi-entry options exist.

## Documents you need

1. **Passport**, valid 6+ months past return, with 2 blank pages.
2. **Completed visa application form** (online at the CVASC portal, then printed).
3. **Recent photo**, white background, 33 × 48 mm, no glasses.
4. **Confirmed return flight** itinerary.
5. **Hotel reservations** for the entire trip (refundable bookings are accepted).
6. **Invitation letter** from a Chinese host **OR** detailed self-arranged itinerary.

## Where to apply

China uses the **Chinese Visa Application Service Centre (CVASC)** network. Centres exist in most major cities including Dubai, Abu Dhabi, London, New York, Sydney.

## Processing time

- **Standard**: 4 working days
- **Express**: 3 working days (+ surcharge)
- **Rush**: 2 working days (+ higher surcharge, where available)

## Fees (USD, vary by nationality)

- Single entry: 30 - 140
- Double entry: 45 - 140
- 6-month multi: 60 - 140
- 12-month multi: 90 - 140

Plus a CVASC service fee of USD 50-80.

## Common mistakes

- Sending the form with handwritten edits — not accepted.
- Photo with glasses, smile or hair covering ears — rejected.
- Itinerary without hotel addresses — flagged.
- Old passport with prior China visas — bring it.

## Skip the queue

Our team handles **end-to-end L visa applications** for trips we plan, including invitation letters, hotel confirmations and CVASC submission. Most clients receive their passport back in 5-7 working days. [Talk to us](/#contact).`,
  },
];

export const getPost = (slug) => posts.find((p) => p.slug === slug);
export const getPostsByCategory = (catSlug) =>
  posts.filter((p) => p.category === catSlug);
export const getRelatedPosts = (slug, n = 3) => {
  const p = getPost(slug);
  if (!p) return [];
  return posts
    .filter((x) => x.slug !== slug && x.category === p.category)
    .slice(0, n);
};
